import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, BarChart3, Eye, Users, MousePointerClick, Compass } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { apiSuperAdmin, mensajeError } from "../../api/client";
import { Spinner } from "../../components/ui/Spinner";
import { RelojFechaHora } from "../../components/ui/RelojFechaHora";
import { useSuperAdminAuth } from "../../context/SuperAdminAuthContext";

interface PuntoSerie {
  fecha: string;
  vistas: number;
  visitantesUnicos: number;
  navegaciones: number;
  clics: number;
}

interface ItemRanking {
  elemento: string;
  total: number;
}

interface ResumenAnalitica {
  dias: number;
  totales: { vistas: number; visitantesUnicos: number; clics: number; navegaciones: number };
  serieDiaria: PuntoSerie[];
  topClics: ItemRanking[];
  topNavegacion: ItemRanking[];
}

const OPCIONES_RANGO = [
  { valor: 7, etiqueta: "Últimos 7 días" },
  { valor: 30, etiqueta: "Últimos 30 días" },
  { valor: 90, etiqueta: "Últimos 90 días" },
];

function formatoFechaCorta(fecha: string) {
  const [, m, d] = fecha.split("-");
  return `${d}/${m}`;
}

function TarjetaTotal({ icono: Icono, etiqueta, valor, color }: { icono: any; etiqueta: string; valor: number; color: string }) {
  return (
    <div className="card flex items-center gap-3 p-4">
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${color}`}>
        <Icono size={20} />
      </span>
      <div>
        <p className="text-xs font-medium text-slate-500">{etiqueta}</p>
        <p className="text-xl font-semibold text-slate-800">{valor.toLocaleString("es-MX")}</p>
      </div>
    </div>
  );
}

function Ranking({ titulo, items }: { titulo: string; items: ItemRanking[] }) {
  const maximo = items[0]?.total || 1;
  return (
    <div className="card p-5">
      <h3 className="mb-4 text-sm font-semibold text-slate-700">{titulo}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400">Todavía no hay datos suficientes en este rango.</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => (
            <div key={it.elemento}>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="truncate pr-2 font-medium text-slate-600">{it.elemento}</span>
                <span className="shrink-0 font-semibold text-slate-700">{it.total}</span>
              </div>
              <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100">
                <div
                  className="h-1.5 rounded-full bg-blue-600"
                  style={{ width: `${Math.max(4, (it.total / maximo) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Analitica() {
  const { superAdmin, cargando: cargandoAuth } = useSuperAdminAuth();
  const [dias, setDias] = useState(30);
  const [resumen, setResumen] = useState<ResumenAnalitica | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let activo = true;
    async function cargar() {
      setCargando(true);
      setError("");
      try {
        const res = await apiSuperAdmin.get("/superadmin/analitica/resumen", { params: { dias } });
        if (activo) setResumen(res.data);
      } catch (err) {
        if (activo) setError(mensajeError(err));
      } finally {
        if (activo) setCargando(false);
      }
    }
    cargar();
    return () => {
      activo = false;
    };
  }, [dias]);

  if (!cargandoAuth && !superAdmin) return <Navigate to="/superadmin/login" replace />;

  return (
    <div className="fondo-marca-agua min-h-screen">
      <header className="flex items-center justify-between bg-gradient-to-r from-slate-900 via-slate-900 to-blue-950 px-6 py-4 text-white">
        <Link to="/superadmin" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
          <ArrowLeft size={15} /> Volver al panel
        </Link>
        <RelojFechaHora claro />
      </header>

      <main className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-800">
              <BarChart3 size={22} className="text-blue-600" /> Analítica de mascotas.vet
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Vistas, navegación y clics del portal de inicio público — anónimo, sin datos personales.
            </p>
          </div>
          <select className="input !w-auto" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
            {OPCIONES_RANGO.map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.etiqueta}
              </option>
            ))}
          </select>
        </div>

        {cargando ? (
          <div className="flex justify-center py-20">
            <Spinner />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : resumen ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <TarjetaTotal icono={Eye} etiqueta="Vistas totales" valor={resumen.totales.vistas} color="bg-blue-50 text-blue-600" />
              <TarjetaTotal icono={Users} etiqueta="Visitantes únicos" valor={resumen.totales.visitantesUnicos} color="bg-teal-50 text-teal-600" />
              <TarjetaTotal icono={Compass} etiqueta="Navegación (clics de menú)" valor={resumen.totales.navegaciones} color="bg-amber-50 text-amber-600" />
              <TarjetaTotal icono={MousePointerClick} etiqueta="Clics en botones" valor={resumen.totales.clics} color="bg-violet-50 text-violet-600" />
            </div>

            <div className="card p-5">
              <h3 className="mb-4 text-sm font-semibold text-slate-700">Vistas y visitantes únicos por día</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={resumen.serieDiaria.map((p) => ({ ...p, fecha: formatoFechaCorta(p.fecha) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="vistas" name="Vistas" stroke="#2563eb" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="visitantesUnicos" name="Visitantes únicos" stroke="#0d9488" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card p-5">
              <h3 className="mb-4 text-sm font-semibold text-slate-700">Navegación y clics por día</h3>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={resumen.serieDiaria.map((p) => ({ ...p, fecha: formatoFechaCorta(p.fecha) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="navegaciones" name="Navegación" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="clics" name="Clics" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Ranking titulo="Botones más clicados" items={resumen.topClics} />
              <Ranking titulo="Secciones más visitadas (menú)" items={resumen.topNavegacion} />
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
